/* ============================================================
   AuthPage — login / register with form toggle
   ============================================================ */

import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext';
import { readStashedRef } from './referralView';

interface Props {
  /** Called after successful login/register (usually to go back). */
  onDone: () => void;
}

type Mode = 'login' | 'register';

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 48,
  padding: '12px 16px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  transition: 'border-color var(--transition-fast)',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--color-danger)',
};

export function AuthPage({ onDone }: Props) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [city, setCity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRegister = mode === 'register';

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      if (isRegister) {
        await register({
          email: email.trim(),
          password,
          displayName: displayName.trim(),
          city: city.trim(),
          // 2f9c70ba: attach the stashed referral token so the backend
          // attributes the new account to the referring performer.
          refCode: readStashedRef(window.localStorage)?.code,
        });
      } else {
        await login({ email: email.trim(), password });
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось выполнить вход');
    } finally {
      setSubmitting(false);
    }
  };

  const emailInvalid = email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(email.trim());
  const passwordInvalid = isRegister && password !== '' && password.length < 8;
  const canSubmit =
    !submitting &&
    email.trim() !== '' &&
    !emailInvalid &&
    password !== '' &&
    !passwordInvalid &&
    (!isRegister || (displayName.trim() !== '' && city.trim() !== ''));

  return (
    <div className="container" style={{ paddingTop: 'var(--space-2xl)', paddingBottom: 'var(--space-2xl)', maxWidth: 480 }}>
      <div className="card" style={{ padding: 'var(--space-xl)' }}>
        <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em', textAlign: 'center' }}>
          {isRegister ? 'Создать аккаунт' : 'Вход'}
        </h1>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', textAlign: 'center', marginTop: 'var(--space-xs)', marginBottom: 'var(--space-lg)' }}>
          {isRegister
            ? 'Аккаунт нужен, чтобы сохранять заказы и получать отклики исполнителей'
            : 'Войдите, чтобы отслеживать свои заказы на печать'}
        </p>

        {/* Mode toggle (segmented control) */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-xs)',
            marginBottom: 'var(--space-lg)',
            background: 'var(--color-separator)',
            borderRadius: 'var(--radius-lg)',
            padding: 3,
          }}
        >
          <SegmentButton active={mode === 'login'} onClick={() => switchMode('login')} label="Вход" />
          <SegmentButton active={mode === 'register'} onClick={() => switchMode('register')} label="Регистрация" />
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {isRegister && (
            <Field label="Имя" required error={null}>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                autoComplete="name"
                placeholder="Как к вам обращаться"
                style={inputStyle}
              />
            </Field>
          )}

          {isRegister && (
            <Field label="Город" required error={null}>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                autoComplete="address-level2"
                placeholder="Москва"
                style={inputStyle}
              />
            </Field>
          )}

          <Field label="Email" required error={emailInvalid ? 'Введите корректный email' : null}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
              style={emailInvalid ? inputErrorStyle : inputStyle}
            />
          </Field>

          <Field
            label="Пароль"
            required
            error={passwordInvalid ? 'Минимум 8 символов' : null}
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              placeholder={isRegister ? 'Минимум 8 символов' : 'Ваш пароль'}
              style={passwordInvalid ? inputErrorStyle : inputStyle}
            />
          </Field>

          {error && (
            <p
              role="alert"
              style={{
                fontSize: 'var(--fs-caption)',
                color: 'var(--color-danger)',
                background: 'rgba(255, 59, 48, 0.08)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
              }}
            >
              {error}
            </p>
          )}

          <button type="submit" className="btn btn-primary" disabled={!canSubmit} style={{ marginTop: 'var(--space-xs)' }}>
            {submitting ? 'Подождите…' : isRegister ? 'Зарегистрироваться' : 'Войти'}
          </button>

          <button
            type="button"
            className="btn btn-tertiary"
            onClick={() => switchMode(isRegister ? 'login' : 'register')}
          >
            {isRegister ? 'Уже есть аккаунт? Войти' : 'Нет аккаунта? Зарегистрироваться'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Reusable field wrapper (matches ProviderProfilePanel) ── */

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
        {label}{required && ' *'}
      </span>
      {children}
      {error && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</span>}
    </label>
  );
}

/* ── Segmented control button (matches ProviderPortal tabs) ── */

function SegmentButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        height: 36,
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--fs-body)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        background: active ? 'var(--color-surface)' : 'transparent',
        boxShadow: active ? 'var(--shadow-card)' : 'none',
        transition: 'background var(--transition-fast), color var(--transition-fast)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
